/**
 * Top bar parts Keystone adds, composed from the kit's own: a TopBarButton that is a link, and the
 * Breadcrumb made to fit a phone and a finger, its place named by its icon. See docs/design/kit-requests.md.
 */
import type { ComponentProps } from "react";
import { Button } from "./Button";
import { Graphic, Icon, type IconName } from "./icons";
import { Tooltip } from "./Tooltip";
import { Breadcrumb } from "./TopBar";

/**
 * The kit Breadcrumb, fitted from outside: its links get a 24 px box, the current page truncates
 * instead of pushing the bar wider, and below `sm` only the current page shows.
 */
export function Trail({ items }: ComponentProps<typeof Breadcrumb>) {
  // Breadcrumb draws an icon only on a link; a page that is its own first crumb (Today, Email) shows its icon here, so every trail starts with one.
  const lone = items.length === 1 ? items[0]?.icon : undefined;
  return (
    <div className="flex min-w-0 items-center gap-2.5 [&_[aria-current]]:max-w-[38vw] [&_[aria-current]]:truncate [&_[aria-current]]:py-1 [&_a]:-my-1 [&_a]:py-1 max-sm:[&_a+span]:hidden max-sm:[&_a]:hidden">
      {lone && <Icon name={lone} />}
      <Breadcrumb items={items} />
    </div>
  );
}

const MOTION = {
  /** Swings from its top like a bell (TopBarButton's). */
  ring: "origin-top transition-transform group-hover/tb:animate-ring",
  /** A quarter turn, like a cog (TopBarButton's). */
  turn: "transition-transform duration-200 ease-out-smooth group-hover/tb:rotate-90",
};

interface TopBarLinkProps {
  /** The link's name and tooltip. */
  label: string;
  icon: IconName;
  href: string;
  motion?: keyof typeof MOTION;
  /** Something waits behind the link: the reference's own dot, in the corner. */
  dot?: boolean;
}

/** TopBarButton as a link: the place opens in a new tab like any other, and it carries no menu to repeat what the page lists. */
export function TopBarLink({ label, icon, href, motion, dot = false }: TopBarLinkProps) {
  return (
    <Tooltip content={label}>
      <Button asChild variant="ghost" size="icon-sm" className="group/tb -m-1">
        <a href={href} aria-label={label}>
          <Icon name={icon} className={motion && MOTION[motion]} />
          {dot && (
            <span aria-hidden className="absolute right-0.5 top-0.5 flex size-1.5 animate-pop">
              <Graphic name="dot" className="block size-full" />
            </span>
          )}
        </a>
      </Button>
    </Tooltip>
  );
}
